// frontend/src/components/admin/RequetesLentes.jsx
// ============================================================================
// REQUETES LENTES
// ----------------------------------------------------------------------------
// Ce que MySQL passe son temps a faire, lu dans ses propres compteurs. Le tri
// par defaut est le temps moyen, mais le tri le plus instructif est souvent
// « temps total » : une requete de 30 ms appelee cent mille fois coute plus
// cher qu'une requete d'une seconde appelee trois fois.
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Timer, RefreshCw, RotateCcw, AlertTriangle, Search } from 'lucide-react';
import superAdminApi from '../../services/superAdminApi';
import { useTheme } from '../../contexts/ThemeContext';

const TRIS = [
  { valeur: 'moyenne', libelle: 'Temps moyen' },
  { valeur: 'total', libelle: 'Temps total' },
  { valeur: 'lignes', libelle: 'Lignes lues' },
  { valeur: 'appels', libelle: "Nombre d'appels" }
];

const nombre = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString('fr-FR') : '—');

const duree = (ms) => {
  const v = Number(ms);
  if (!Number.isFinite(v)) return '—';
  if (v >= 60000) return `${(v / 60000).toFixed(1)} min`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)} s`;
  return `${v.toFixed(1)} ms`;
};

const Carte = ({ libelle, valeur, detail, t }) => (
  <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14,
    padding: '14px 18px' }}>
    <p style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, margin: 0,
      textTransform: 'uppercase', letterSpacing: '0.5px' }}>{libelle}</p>
    <p style={{ fontSize: 20, fontWeight: 800, color: t.text, margin: '2px 0 0' }}>{valeur}</p>
    {detail && <p style={{ fontSize: 12, color: t.textMuted, margin: '2px 0 0' }}>{detail}</p>}
  </div>
);

const RequetesLentes = () => {
  const t = useTheme();
  const [etat, setEtat] = useState(null);
  const [tri, setTri] = useState('moyenne');
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [message, setMessage] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const r = await superAdminApi.requetes.etat(tri, 25);
      setEtat(r.data || null);
    } catch (e) {
      setErreur(e.message || 'Lecture impossible');
    } finally {
      setChargement(false);
    }
  }, [tri]);

  useEffect(() => { charger(); }, [charger]);

  const remettreAZero = async () => {
    setMessage(null);
    try {
      const r = await superAdminApi.requetes.remettreAZero();
      setMessage(r.success
        ? 'Compteurs remis à zéro : la mesure repart de maintenant.'
        : `${r.raison} Pour l'autoriser : ${r.commande}`);
      if (r.success) charger();
    } catch (e) {
      setMessage(e.message || 'Remise à zéro impossible');
    }
  };

  const r = etat?.resume;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: t.text, margin: 0,
            display: 'flex', alignItems: 'center', gap: 9 }}>
            <Timer size={20} style={{ color: t.primary }} />
            Requêtes lentes
          </h2>
          <p style={{ fontSize: 13, color: t.textMuted, margin: '4px 0 0' }}>
            {r ? `${nombre(r.formes)} formes de requête observées depuis le ${r.depuis}`
               : 'Lecture des compteurs…'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select value={tri} onChange={(e) => setTri(e.target.value)}
            style={{ background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: 10,
              padding: '9px 12px', fontSize: 13, color: t.text, fontFamily: 'inherit' }}>
            {TRIS.map((o) => <option key={o.valeur} value={o.valeur}>Trier par {o.libelle.toLowerCase()}</option>)}
          </select>
          <button type="button" onClick={remettreAZero}
            style={{ background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: 10,
              padding: '9px 14px', fontSize: 13, fontWeight: 600, color: t.text, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'inherit' }}>
            <RotateCcw size={14} />
            Repartir de maintenant
          </button>
          <button type="button" onClick={charger}
            style={{ background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: 10,
              padding: '9px 14px', fontSize: 13, fontWeight: 600, color: t.text, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'inherit' }}>
            <RefreshCw size={14} style={{ animation: chargement ? 'spin 1s linear infinite' : 'none' }} />
            Actualiser
          </button>
        </div>
      </div>

      {message && (
        <div style={{ background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: 12,
          padding: '12px 16px', fontSize: 13, color: t.textSecondary }}>{message}</div>
      )}

      {erreur && (
        <div style={{ background: t.dangerSoft, border: `1px solid ${t.danger}30`, borderRadius: 14,
          padding: '14px 18px', fontSize: 14, color: t.dangerText }}>{erreur}</div>
      )}

      {r && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
          <Carte libelle="Requêtes exécutées" valeur={nombre(r.appels)} t={t} />
          <Carte libelle="Temps passé en base" valeur={duree(r.temps_total_ms)} t={t} />
          <Carte libelle="Lignes lues" valeur={nombre(r.lignes_lues)} t={t} />
          <Carte libelle="Appels sans index" valeur={nombre(r.appels_sans_index)}
            detail={r.appels ? `${Math.round((r.appels_sans_index / r.appels) * 100)} % des appels` : null} t={t} />
        </div>
      )}

      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14, overflow: 'hidden' }}>
        {chargement && !etat && (
          <p style={{ margin: 0, padding: 18, fontSize: 14, color: t.textMuted }}>Lecture…</p>
        )}

        {etat?.requetes?.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: t.bgTableHeader }}>
                {['Requête', 'Appels', 'Moyenne', 'Pire', 'Total', 'Lues / rendues'].map((titre) => (
                  <th key={titre} style={{ textAlign: 'left', padding: '11px 16px', fontSize: 11,
                    fontWeight: 700, color: t.textMuted, textTransform: 'uppercase',
                    letterSpacing: '0.5px', borderBottom: `1px solid ${t.border}` }}>{titre}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {etat.requetes.map((q) => {
                const gourmande = q.rendement !== null && q.rendement >= 100;
                return (
                  <tr key={q.empreinte} style={{ borderBottom: `1px solid ${t.borderLight}` }}>
                    <td style={{ padding: '11px 16px', maxWidth: 460 }}>
                      <code style={{ fontSize: 12, color: t.text, wordBreak: 'break-word',
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                        {q.requete}
                      </code>
                      <span style={{ display: 'block', fontSize: 11, color: t.textMuted, marginTop: 3 }}>
                        vue du {q.vue_pour_la_premiere_fois} au {q.vue_pour_la_derniere_fois}
                        {q.sans_index > 0 ? ` · ${nombre(q.sans_index)} appel(s) sans index` : ''}
                        {q.tables_temporaires_disque > 0
                          ? ` · ${nombre(q.tables_temporaires_disque)} table(s) temporaire(s) sur disque` : ''}
                      </span>
                    </td>
                    <td style={{ padding: '11px 16px', fontSize: 13, color: t.textSecondary,
                      fontVariantNumeric: 'tabular-nums' }}>{nombre(q.appels)}</td>
                    <td style={{ padding: '11px 16px', fontSize: 13, fontWeight: 600, color: t.text,
                      fontVariantNumeric: 'tabular-nums' }}>{duree(q.moyenne_ms)}</td>
                    <td style={{ padding: '11px 16px', fontSize: 13, color: t.textSecondary,
                      fontVariantNumeric: 'tabular-nums' }}>{duree(q.pire_ms)}</td>
                    <td style={{ padding: '11px 16px', fontSize: 13, color: t.textSecondary,
                      fontVariantNumeric: 'tabular-nums' }}>{duree(q.total_ms)}</td>
                    <td style={{ padding: '11px 16px', fontSize: 13,
                      color: gourmande ? t.warningText : t.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {gourmande && <AlertTriangle size={13} />}
                        {q.rendement === null ? '—' : `${nombre(q.rendement)} pour 1`}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {etat && !etat.requetes.length && !chargement && (
          <p style={{ margin: 0, padding: 18, fontSize: 14, color: t.textMuted,
            display: 'flex', alignItems: 'center', gap: 8 }}>
            <Search size={15} /> Aucune requête enregistrée pour cette base.
          </p>
        )}
      </div>

      <p style={{ fontSize: 12, color: t.textMuted, margin: 0, lineHeight: 1.6 }}>
        « Lues / rendues » est le rapport entre les lignes que MySQL a dû parcourir et celles
        qu&rsquo;il a renvoyées : au-delà de cent pour une, il manque presque toujours un index.
        Les compteurs repartent de zéro au redémarrage de la base.
      </p>
    </div>
  );
};

export default RequetesLentes;
